import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ShieldCheck, Users2, Check, Loader2 } from "lucide-react";
import {
  PageContainer,
  PageHeader,
  Card,
  Pill,
} from "@/components/layout/PageShell";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import api from "@/lib/api";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

export default function RolesPage() {
  const queryClient = useQueryClient();
  const [activeId, setActiveId] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["roles"],
    queryFn: async () => {
      const { data } = await api.get("/roles");
      return data.roles;
    },
  });

  const { data: modules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ["permissions"],
    queryFn: async () => {
      const { data } = await api.get("/permissions");
      return data.modules;
    },
  });

  const { data: roleDetail, isLoading: detailLoading } = useQuery({
    queryKey: ["roles", activeId],
    queryFn: async () => {
      const { data } = await api.get(`/roles/${activeId}`);
      return data.role;
    },
    enabled: Boolean(activeId),
  });

  useEffect(() => {
    if (!activeId && roles.length) {
      setActiveId(roles[0].id);
    }
  }, [roles, activeId]);

  useEffect(() => {
    if (roleDetail?.permission_slugs) {
      setSelected(new Set(roleDetail.permission_slugs));
    }
  }, [roleDetail]);

  const allActions = useMemo(() => {
    const actions = new Set();
    modules.forEach((m) => m.actions.forEach((a) => actions.add(a.action)));
    return Array.from(actions);
  }, [modules]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      return api.put(`/roles/${activeId}/permissions`, {
        permission_slugs: Array.from(selected),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles"] });
      queryClient.invalidateQueries({ queryKey: ["roles", activeId] });
      toast.success("Role permissions saved");
    },
    onError: () => toast.error("Failed to save permissions"),
  });

  function toggle(slug) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
  }

  const activeRole = roles.find((r) => r.id === activeId);
  const loading = rolesLoading || modulesLoading || detailLoading;

  return (
    <PageContainer>
      <PageHeader title="Roles & Permissions" subtitle="Define what each role can see and do" />
      <div className="grid grid-cols-1 xl:grid-cols-[300px_1fr] gap-4">
        <Card className="p-0 h-fit">
          <div className="px-3 py-2.5 border-b text-[12px] font-semibold text-slate-600 uppercase tracking-wide">
            Roles
          </div>
          {rolesLoading ? (
            <div className="p-6 grid place-items-center text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <ul className="p-2 space-y-1">
              {roles.map((r) => (
                <li key={r.id}>
                  <button
                    onClick={() => setActiveId(r.id)}
                    data-testid={`role-${r.slug}`}
                    className={cn(
                      "w-full text-left px-3 py-2 rounded-md border",
                      activeId === r.id
                        ? "bg-blue-50 border-blue-200"
                        : "bg-white border-transparent hover:bg-slate-50",
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-[13px] font-semibold text-slate-800">{r.name}</span>
                      <Pill tone="blue">{r.permissions_count} perms</Pill>
                    </div>
                    <div className="text-[11.5px] text-slate-500 mt-0.5">{r.description}</div>
                    <div className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                      <Users2 size={11} /> {r.users_count} users assigned
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="p-0">
          <div className="px-4 py-3 border-b flex items-center justify-between">
            <div>
              <div className="text-[15px] font-semibold text-slate-900 flex items-center gap-2">
                <ShieldCheck className="text-blue-600" size={16} /> {activeRole?.name || "—"} Permissions
              </div>
              <div className="text-[11.5px] text-slate-500">{activeRole?.description}</div>
            </div>
            <Button
              size="sm"
              className="h-8 bg-[#16A34A] hover:bg-emerald-700"
              data-testid="roles-save"
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending || !activeId}
            >
              {saveMutation.isPending ? (
                <Loader2 size={13} className="mr-1.5 animate-spin" />
              ) : (
                <Check size={13} className="mr-1.5" />
              )}
              Save Changes
            </Button>
          </div>
          <div className="overflow-auto">
            {loading ? (
              <div className="h-40 grid place-items-center text-slate-500">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            ) : (
              <table className="w-full text-[12.5px]">
                <thead className="bg-slate-50 text-slate-500 border-b sticky top-0">
                  <tr>
                    <th className="text-left font-medium px-4 py-2.5 w-[180px]">Module</th>
                    {allActions.map((a) => (
                      <th key={a} className="text-center font-medium px-3 py-2.5">{a}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {modules.map((m) => (
                    <tr key={m.module} className="border-t hover:bg-slate-50/60">
                      <td className="px-4 py-2.5 font-medium text-slate-800">{m.module}</td>
                      {allActions.map((action) => {
                        const perm = m.actions.find((a) => a.action === action);
                        return (
                          <td key={action} className="text-center px-3 py-2.5">
                            {perm ? (
                              <Checkbox
                                checked={selected.has(perm.slug)}
                                onCheckedChange={() => toggle(perm.slug)}
                                data-testid={`perm-${perm.slug}`}
                              />
                            ) : (
                              <span className="text-slate-300">—</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
